import { useEffect, useMemo, useState } from 'react';
import WrapperDataGrid from '@modules/dataGrid/WrapperDataGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { IDataGridConfig, EntityCell, TInheritedProps } from '@ts/storybook';
import { generateGlobalColumns, generateBasicColumn, generateExpanableColumn } from '@ts/storybook';
import Items, { defaultConfigComponent as ItConfigComponent } from '@pages/DataBase/Flavors/Items/Items';
import { useAppDispatch } from '@redux/store';
import { CElementType, IConfigComponent, CElement } from '@ts/ui-models';
import useFetchElementType from '@utils/hooks/fetchData/useFetchElementType';
import { getLocation } from '@services/DataBase/Element.service';

interface IGTItemTypesInLocationProps<Multiple extends boolean> {
  tElement: CElement;
  configComponent?: IConfigComponent;
  inheritedProps?: TInheritedProps<Multiple>;
  title?: string;
}

export const defaultConfigComponent: IConfigComponent = {
  componentId: 'itemTypeCollapsable',
  aclId: 'tsElementType',
  //entity: 'tsElementType',
  fetchURL: '/api/v2/location/locationId',
};

const GTItemTypesInLocation = <Multiple extends boolean>(props: IGTItemTypesInLocationProps<Multiple>) => {
  const { configComponent = defaultConfigComponent } = props;
  const config: IDataGridConfig = {
    columnVisibilityModel: { id: false },
    sortModel: [{ field: 'name', sort: 'asc' }],
  };
  const dispatch = useAppDispatch();
  const [itemsInsideByType, setItemsInsideByType] = useState<{ itemTypeId: string; count: number }[] | null>(null);

  const getColumns = useMemo((): GridColDef<any>[] => {
    const globalCells = generateGlobalColumns({ ...props, configComponent });
    return [
      generateExpanableColumn(() => true),
      globalCells.id(),
      {
        headerName: 'Name / ID',
        field: 'name',
        type: 'string',
        valueGetter: (value, row, column) => {
          return row.itemTypeId;
        },
        renderCell: (params: GridRenderCellParams) => {
          const elementType = params.row.itemTypeId === 'Without type' ? null : useFetchElementType(params.row.itemTypeId);
          if (!elementType) return 'Without type';
          const tElement = new CElementType(elementType);

          return <EntityCell entity="itemType" cEntity={tElement} />;
        },
      },
      generateBasicColumn({ field: 'count' }, { field: 'count' }),
    ];
  }, [props, configComponent, props.tElement]);

  //#region Render
  const renderCollapsable = (propsCollapsable: { row: any; inheritedProps?: TInheritedProps<false> }) => {
    return (
      <Items
        key="itemTypeCollapsable"
        configComponent={{
          ...ItConfigComponent,
          where: { elementTypeId: propsCollapsable.row.itemTypeId === 'Without type' ? null : propsCollapsable.row.itemTypeId },
          filtersFetchData: {
            xlocs: {
              parentElementId: props.tElement.getID(),
            },
          },
        }}
        inheritedProps={propsCollapsable.inheritedProps}
      />
    );
  };
  const renderOneItemTypeInLocation = () => {
    if (!itemsInsideByType) return;
    return renderCollapsable({
      row: itemsInsideByType[0],
      inheritedProps: { isModal: true, toolbarProps: { inCollapsable: true, withoutSave: true } },
    });
  };
  //#endregion

  useEffect(() => {
    const getItemsInsideByType = async () => {
      if (props.tElement) {
        const location = await getLocation(dispatch, props.tElement.getID());
        if (location?.itemsInsideByType) {
          setItemsInsideByType(
            location.itemsInsideByType.map((d, i) => ({
              id: d?.itemTypeId ?? 'Without type',
              itemTypeId: d?.itemTypeId ?? 'Without type',
              count: d.count,
            }))
          );
        }
      }
    };
    getItemsInsideByType();
  }, []);

  if (itemsInsideByType?.length === 1) return renderOneItemTypeInLocation();
  if (!itemsInsideByType) return;
  return (
    <WrapperDataGrid
      key={'ItemsTypeInside'}
      configComponent={configComponent}
      dataGridConfig={config}
      rows={{ count: null, rows: itemsInsideByType }}
      columns={getColumns}
      dataGridMode="client"
      //rowHeight={40}
      //Extra data
      total={itemsInsideByType?.reduce((a, b) => a + b.count, 0) ?? 0}
      collapsableComponent={renderCollapsable}
      //collapsableConfig={configComponent}
      inheritedProps={
        {
          ...props.inheritedProps,
          isEntitySelector: true,
          toolbarProps: { withoutSave: true },
        } as TInheritedProps<Multiple>
      }
    />
  );
};

export default GTItemTypesInLocation;
